import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * Which half of the fleet list is showing. `aboard` is what the map shows,
 * and what most people open the page for; `ashore` is everything else.
 */
export type FleetView = "aboard" | "ashore";

interface GlobalTrackingState {
	/**
	 * Whether the map tours the fleet on a loop.
	 *
	 * Persisted, because it is a choice about how someone watches the map rather
	 * than a position on a page: whoever leaves this on has it on a wall screen
	 * and wants it running again on the next load, not off.
	 *
	 * `selectedId` deliberately does **not** live here. Which vessel is open is a
	 * question about the map currently on screen, and restoring it days later
	 * would fly the map to a shipment nobody asked about - one that may not even
	 * be tracked any more.
	 */
	animate: boolean;
	/** Persisted for the same reason: it is how this person reads the page. */
	fleetView: FleetView;
}

const initialState: GlobalTrackingState = {
	// Off is the resting state: the tour takes the camera away from whoever is
	// reading the map, so it has to be asked for.
	animate: false,
	fleetView: "aboard",
};

const globalTrackingSlice = createSlice({
	name: "globalTracking",
	initialState,
	reducers: {
		setAnimateVessels: (state, action: PayloadAction<boolean>) => {
			state.animate = action.payload;
		},
		setFleetView: (state, action: PayloadAction<FleetView>) => {
			state.fleetView = action.payload;
		},
	},
});

export const { setAnimateVessels, setFleetView } = globalTrackingSlice.actions;
export default globalTrackingSlice.reducer;
